/**
 * GoogleReviewsBlock — Phase 4 (allégée, sans Places API)
 * Affiche un encart "Avis Google" avec étoiles or, texte admin-configurable
 * (`google_rating_display`), et un CTA vers la fiche Google Business
 * (`google_business_url`) + sous-bouton "Laisser un avis"
 * (`google_review_url`).
 *
 * Tous les champs sont optionnels → si `google_business_url` est vide, le
 * composant ne s'affiche pas (fail-safe).
 */
import React from 'react';
import { Star, ExternalLink, PenSquare } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

function Stars({ count = 5, size = 22 }) {
  return (
    <div style={{ display: 'inline-flex', gap: 4 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Star
          key={i}
          size={size}
          fill="#d4af37"
          stroke="#d4af37"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(212,175,55,0.4))' }}
        />
      ))}
    </div>
  );
}

export default function GoogleReviewsBlock({ settings = {} }) {
  const { t } = useLanguage();
  const {
    google_business_url,
    google_review_url,
    google_rating_display,
  } = settings;

  // Fail-safe: hide the whole block if no business URL is configured
  if (!google_business_url) return null;

  const rating = (google_rating_display || '').trim()
    || t('reviews.ratingFallback');

  return (
    <div
      className="gr-block"
      data-testid="google-reviews-block"
      style={{
        position: 'relative',
        background: 'radial-gradient(ellipse at top, rgba(200,16,46,0.08) 0%, #0a0a0a 65%)',
        border: '1px solid rgba(212,175,55,0.28)',
        borderRadius: 18,
        padding: '28px 24px',
        textAlign: 'center',
        overflow: 'hidden',
        boxShadow: '0 24px 56px rgba(0,0,0,0.5)',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 1,
          background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <Stars count={5} size={26} />

        <div
          style={{
            fontFamily: '-apple-system, "SF Pro Display", "Segoe UI", system-ui, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '-0.01em',
          }}
          data-testid="google-reviews-rating"
        >
          {rating}
        </div>

        <div style={{ fontSize: 13, color: '#aaa', maxWidth: 480, lineHeight: 1.5 }}>
          {t('reviews.subtitle')}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12, marginTop: 4 }}>
          <a
            href={google_business_url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="google-reviews-read-cta"
            className="gr-cta-primary"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '12px 22px',
              background: 'linear-gradient(145deg, #d11833 0%, #8a0a1d 100%)',
              color: '#fff',
              border: '1px solid #d4af37',
              borderRadius: 999,
              fontSize: 14, fontWeight: 700,
              textDecoration: 'none',
              boxShadow: '0 8px 24px rgba(200,16,46,0.35)',
              transition: 'transform .15s ease, box-shadow .15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(200,16,46,0.55)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(200,16,46,0.35)';
            }}
          >
            <Star size={14} fill="#d4af37" stroke="#d4af37" />
            {t('reviews.readCta')}
            <ExternalLink size={13} />
          </a>

          {google_review_url && (
            <a
              href={google_review_url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="google-reviews-write-cta"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 20px',
                background: 'transparent',
                color: '#d4af37',
                border: '1px solid rgba(212,175,55,0.5)',
                borderRadius: 999,
                fontSize: 13, fontWeight: 600,
                textDecoration: 'none',
                transition: 'all .15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(212,175,55,0.12)';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = '#d4af37';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#d4af37';
                e.currentTarget.style.borderColor = 'rgba(212,175,55,0.5)';
              }}
            >
              <PenSquare size={14} />
              {t('reviews.writeCta')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
